import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Checkbox, CircularProgress, Typography } from '@mui/material';
import { loadDataSource } from 'js/actions';
import { DescriptionView } from 'js/components/data_sources/add_ds_form/vendors/email/EmailScriptConfiguration/DescriptionView';
import { SUPPORT_KEY_SOURCE_FORM_EMAIL } from 'js/constants/supportPageKeys';
import { useGetPaging } from 'js/hooks/useGetPaging';
import UIUtils from 'js/lib/UIUtils';
import { filter, has, map } from 'lodash';
import { ReactComponent as EmailIcon } from 'new/components/icons/fi-rr-envelope.svg';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import { TextField } from 'new/elements/TextField';
import { Button } from 'new/elements/support/Button';

import {
  FieldBuilderRedesigned,
  HeaderField,
} from 'new/elements/FieldBuilderRedesigned/FieldBuilder';
import {
  DATA_PROCESSING_SECTION,
  DATASET_CREATE_SECTION,
  EmailScriptConfigurationProps,
  extractOptionsBySection,
} from '.';

import styles from './EmailScriptConfiguration.module.scss';

export function EmailScriptConfiguration(props: EmailScriptConfigurationProps) {
  const { dataSource, sourceBeingEdited, selectedConnection = 'email' } = props;
  const dispatch = useDispatch();
  const { sourceConfigById = {} } = useGetPaging();
  const sourceConfigFields = sourceConfigById[selectedConnection];

  const templatizedScriptConfig = sourceBeingEdited?.id
    ? UIUtils.buildTemplatizedScriptConfigFromSource(sourceBeingEdited, sourceConfigFields, {})
        .templatizedScriptConfig
    : props.templatizedScriptConfig;

  let advancedSettingsOptions = filter(
    sourceConfigFields,
    (field) =>
      field.is_advanced_setting &&
      UIUtils.shouldRenderField(field, templatizedScriptConfig) &&
      UIUtils.shouldHideField(field, templatizedScriptConfig) === false,
  );

  useEffect(() => {
    if (sourceBeingEdited?.id) {
      dispatch(loadDataSource(sourceBeingEdited.id));
    }

    props.setContextualSupport(SUPPORT_KEY_SOURCE_FORM_EMAIL);

    return () => {
      props.unsetContextualSupport(SUPPORT_KEY_SOURCE_FORM_EMAIL);
    };
  }, []);

  function handleOnTextInputChanged(name: string, value: string) {
    props?.onMetaInfoChanged(name, value);
  }

  function handleAdvancedSettingChanged(name: string, value: string | boolean) {
    props.onTemplatizedScriptConfigChanged(props.templatizedScriptConfig || {}, name, value);
  }

  function _renderPostCreateContent() {
    const emailId = (() => {
      if (has(templatizedScriptConfig, 'parameters.email')) {
        return templatizedScriptConfig['parameters.email'];
      } else if (has(dataSource, 'script_config.parameters.email')) {
        return dataSource.script_config.parameters.email;
      }
      return '';
    })();

    const mode = (() => {
      if (has(templatizedScriptConfig, 'parameters.mode')) {
        return templatizedScriptConfig['parameters.mode'];
      } else if (has(dataSource, 'script_config.parameters.mode')) {
        return dataSource.script_config.parameters.mode;
      }
      return '';
    })();

    let modeLabel = '';
    if (mode) {
      const sourceConfigFields = sourceConfigById[selectedConnection];
      const modeField = Object.values(sourceConfigFields).find(
        ({ name }) => name === 'parameters.mode',
      ) as any;

      const modelFieldOptions = modeField?.options ?? [];
      const option = modelFieldOptions.find(({ value }) => value === mode);
      modeLabel = option?.label;
    }

    return (
      <div className={styles.generatedInfo}>
        <div role="group" aria-labelledby="email-id">
          <Typography variant="h6" id="email-id">
            Email ID
          </Typography>
          <div className={styles.row}>
            <div className={styles.data}>{emailId}</div>
            <ChipWithCopy mode="light" toCopy={emailId}>
              <div role="button" className={styles.copyBtn}>
                <ContentCopyIcon />
                Copy
              </div>
            </ChipWithCopy>
          </div>
        </div>

        <div role="group" aria-labelledby="datasetDetectionLabel">
          <Typography id="datasetDetectionLabel" variant="h6">
            Dataset Detection Mode
          </Typography>
          <div className={styles.data}>{modeLabel}</div>
        </div>

        <div role="group" aria-labelledby="nextSteps">
          <Typography variant="h6" id="nextSteps">
            Next Steps
          </Typography>
          <Typography variant="bodyDefault">
            Setup forwarding rules to the uniquely generated Email ID. Nexla will monitor the email
            id for incoming data and detect whether the new email mapped into a new Nexset or an
            existing one. Click the Create button in the top right to finish creating this Data
            Source.
          </Typography>
        </div>
      </div>
    );
  }

  let content: {};

  if (dataSource?.id || sourceBeingEdited?.id) {
    content = _renderPostCreateContent();
    advancedSettingsOptions = map(advancedSettingsOptions, (field) => ({
      ...field,
      disabled: true,
    }));
  } else {
    content = (
      <Button
        data-testid="create-email-source"
        role="button"
        color="secondary"
        variant="outlined"
        disabled={!props.name}
        onClick={props.onCreateSource}
      >
        {props.isCreatingSource ? (
          <CircularProgress className={styles.buttonToCreate} />
        ) : (
          'Create Email Source'
        )}
      </Button>
    );
  }

  const settingsOptionsList = Object.values(advancedSettingsOptions);
  const optionsBySectionsMapped = extractOptionsBySection(settingsOptionsList);

  const dataProcessingOptions = optionsBySectionsMapped[DATA_PROCESSING_SECTION] ?? [];
  const datasetCreationOptions = optionsBySectionsMapped[DATASET_CREATE_SECTION] ?? [];

  return (
    <div className="advanced-configuration-container">
      <div className={styles.container}>
        <div className={styles.categoryHeader}>
          <div className={styles.labelView}>
            <EmailIcon />
            <span className={styles.logoSeparator} />
            <span className={styles.label}>Email</span>
          </div>
        </div>

        <div className={styles.innerContainerContent}>
          <div className={styles.createContentForm}>
            <section className={styles.formView}>
              <div className={styles.section} data-testid="data-source-name">
                <TextField
                  isRequired
                  htmlFor="data-source-name"
                  label="Data Source Name *"
                  placeholder="Enter a name"
                  formControlStyle={{ minWidth: '320px' }}
                  value={props.name || ''}
                  onChange={(event: any) => handleOnTextInputChanged('name', event.target.value)}
                />
              </div>

              <div className={styles.section}>
                <div className={styles.block}>
                  <span className={styles.title}>Data Processing</span>
                  <div
                    data-testid="data-processing-options"
                    className={
                      dataProcessingOptions.length > 2
                        ? styles.containerManyOptions
                        : styles.containerOptions
                    }
                  >
                    {dataProcessingOptions.map((field: any) => {
                      const { name } = field;
                      const defaultValueByConfig = field?.defaultValue ?? field?.default;
                      const currentValue = templatizedScriptConfig[name] ?? defaultValueByConfig;

                      return (
                        <FieldBuilderRedesigned
                          field={field}
                          id={field.name}
                          key={field.display_name}
                          isRequired={field.required}
                          currentFieldValue={currentValue}
                          onChange={handleAdvancedSettingChanged}
                        />
                      );
                    })}
                  </div>
                </div>

                <div>
                  <span className={styles.title}>Dataset Creation</span>
                  {datasetCreationOptions.map((item: any) => {
                    const { name, disabled = false } = item;
                    let currentValue = templatizedScriptConfig[name] ?? false;
                    currentValue = currentValue === 'false' ? false : currentValue;

                    return (
                      <div key={item.display_name} className={styles.inlineInput}>
                        <Checkbox
                          name={item.display_name}
                          className={styles.padding0}
                          disabled={disabled}
                          checked={currentValue}
                          onChange={(_, value) => handleAdvancedSettingChanged(item.name, value)}
                        />
                        <HeaderField item={item} className={styles.label400} />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className={styles.section} style={{ border: 0 }}>
                {content}
              </div>
            </section>

            <DescriptionView />
          </div>
        </div>
      </div>
    </div>
  );
}
